import { useMemo } from 'react';

import {
  appointmentDataAtom,
  appointmentDataWithResetAtom,
} from '@repo/videa-notes';
import { useAtomValue, useSetAtom } from 'jotai';

import { formatPatientFirstNameAndLastName } from '@/utils/patient';

import { getPatientName } from '../../uploader/patientList/patientListTableContainer/utils';

/**
 * Hook for accessing and deriving appointment data for Videa Notes.
 * Extracts patient info, visit IDs, and computes display names.
 */
export const useAppointmentData = (isDisplayPhi: boolean) => {
  const appointmentData = useAtomValue(appointmentDataAtom);
  const setAppointmentData = useSetAtom(appointmentDataWithResetAtom);

  const patient = appointmentData?.patient;
  const estimatedVisitId = appointmentData?.estimatedVisitId;
  const unifiedAppointmentId = appointmentData?.unifiedId;
  const standalonePatientVisitId = appointmentData?.standalonePatientVisitId;
  const appointmentStartsAt = appointmentData?.appointmentStartsAt;

  const patientName = useMemo(
    () =>
      getPatientName(
        formatPatientFirstNameAndLastName(
          patient?.firstName,
          patient?.lastName,
        ),
        0,
        isDisplayPhi,
      ),
    [patient?.firstName, patient?.lastName, isDisplayPhi],
  );

  const patientNameForConsentRecord = useMemo(
    () =>
      getPatientName(
        formatPatientFirstNameAndLastName(
          patient?.firstName,
          patient?.lastName,
        ),
        0,
        true, // Always show full name for consent records
      ),
    [patient?.firstName, patient?.lastName],
  );

  return {
    // Raw appointment data
    appointmentData,
    setAppointmentData,

    // Patient info
    patient,
    patientName,
    patientNameForConsentRecord,

    // Visit IDs
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,

    // Timing
    appointmentStartsAt,
  };
};
